import { useApolloClient } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import { Box } from 'reflexbox';
import _ from 'lodash';
import styled from 'styled-components';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { Paragraph } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Container from 'packages/kakapo-ui-kit/components/grid/Container';
import IntercomTarget from 'packages/kakapo-ui-application/common/containers/IntercomTarget';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import { isCycleDue } from 'packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';

import InboxManagerItem from './InboxManagerItem';
import InboxManagerHeader from './InboxManagerHeader';
import InboxManagerSidebar from './InboxManagerSidebar';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import InboxManagerCsvExport from './InboxManagerCsvExport';

const Split = styled.div`
	display: flex;
	flex-direction: row;
	height: 100%;
`;

const Main = styled.div`
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
`;

const Sidebar = styled(Box)`
	background: white;
	border-left: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	min-height: calc(100vh - 71px);
	flex-shrink: 0;
`;

const Table = styled.div`
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
	flex-direction: column;
	height: 100%;
`;

const TableContents = styled.div`
	position: relative;
	flex-grow: 1;
	flex-shrink: 1;
`;

export function InboxManagerContainerWithoutSidebar(props: { header?: boolean }) {
	const { t } = useTranslation();

	const roles = useViewerRoles();

	const formik = useFormik({
		initialValues: {
			status: [graphql.UserStatus.Active],
			query: '',
			groupIDs: [],
			cycleTemplateIDs: [],
			jobTitleIDs: [],
			recurse: false,
			onlyDue: false,
			startDate: moment().startOf('day').subtract(2, 'months').toDate(),
			endDate: moment().startOf('day').add(5, 'months').toDate(),
		},
		onSubmit: () => {},
	});

	const dateRange: [Date, Date] = [formik.values.startDate, formik.values.endDate];

	const { loading, results } = useSearch(formik.values.query, {
		filters: formik.values.groupIDs.map((id) => `groupID:${id}`).join(' OR '),
		status: formik.values.status,
	});

	const query = graphql.useManagerInboxQuery({
		fetchPolicy: 'network-only',
		variables: {
			recurse: formik.values.recurse,
		},
		skip: roles.isWorkspaceSuperuser,
	});

	const managees = new Set(query.data?.viewer.managees.map(({ id }) => id) ?? []);

	const users = _.sortBy(
		(results ?? []) as graphql.User[],
		(user) => `${user.firstName} ${user.lastName}`,
	).filter(({ id }) => roles.isWorkspaceSuperuser || managees.has(id));

	const [selected, setSelected] = useState<string | undefined>();

	useEffect(() => {
		setSelected(undefined);
	}, [formik.values.startDate, formik.values.endDate]);

	const [details, setDetails] = useState(new Map());

	const filtered = users.filter((user) => {
		if (!formik.values.onlyDue) {
			return true;
		}

		const detail = details.get(user.id);

		if (!detail) {
			return true;
		}

		return isCycleDue(detail, roles.isWorkspaceSuperuser, dateRange);
	});

	const selectedHeight = useRef(0);

	const ref = useRef<VariableSizeList>(null);

	useEffect(() => {
		ref.current?.resetAfterIndex(0, true);
	}, [selected]);

	const client = useApolloClient();

	useEffect(() => {
		(async () => {
			const chunks = _.chunk(
				users.map((user) => user.id).filter((id) => !details.has(id)),
				10,
			);

			for (const ids of chunks) {
				const response = await client.query<graphql.ManagerInboxDetailQuery>({
					query: graphql.ManagerInboxDetailDocument,
					fetchPolicy: 'network-only',
					variables: { ids },
				});

				setDetails((details) => {
					const copy = new Map(details);

					for (const user of response.data.usersByIDs) {
						copy.set(user.id, user);
					}

					return copy;
				});
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [users.map((user) => user.id).join(',')]);

	const [exporting, setExporting] = useState(false);

	const [sidebar, setSidebar] = useState(false);

	return (
		<>
			<AutoSizer disableHeight style={{ height: '100%' }}>
				{({ width }) => (
					<Box width={width} height="100%" flexGrow={1}>
						{/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
						{/*@ts-ignore*/}
						{width < 900 ? (
							<InboxManagerHeader
								small
								sidebar={false}
								header={props.header}
								toggleSidebar={() => {}}
							/>
						) : (
							<IntercomTarget
								target="manager-inbox"
								block
								style={{
									height: '100%',
								}}
							>
								<Split>
									<Main>
										<Container
											fluid
											style={{
												flexGrow: 1,
												height: '100%',
											}}
										>
											<Box
												style={{
													flexShrink: 1,
													flexGrow: 1,
													height: '100%',
												}}
											>
												{loading || query.loading ? (
													<>
														<InboxManagerHeader
															sidebar={sidebar}
															header={props.header}
															toggleSidebar={() => setSidebar(!sidebar)}
														/>
														<Spinner isLoading size="18px" />
													</>
												) : users.length === 0 ? (
													<>
														<InboxManagerHeader
															sidebar={sidebar}
															header={props.header}
															toggleSidebar={() => setSidebar(!sidebar)}
														/>
														<Box px={4}>
															<Paragraph textAlign="center">
																{t('employees.search.empty')}
															</Paragraph>
														</Box>
													</>
												) : (
													<Table>
														<TableContents>
															<AutoSizer>
																{({ height, width }) => (
																	<VariableSizeList
																		ref={ref}
																		width={width}
																		height={height}
																		itemCount={filtered.length + 1}
																		estimatedItemSize={40}
																		itemSize={(index) =>
																			index === 0
																				? props.header === false
																					? 300
																					: 410
																				: filtered[index - 1].id === selected
																				? 40 + selectedHeight.current
																				: 40
																		}
																		itemData={{
																			dateRange,
																			results: filtered,
																			details,
																			selected,
																			setSelected: (id) => {
																				selectedHeight.current = 0;
																				setSelected(id);
																			},
																			setHeight: (height) => {
																				selectedHeight.current = height;
																				ref.current?.resetAfterIndex(0, true);
																			},
																			header: props.header,
																			sidebar,
																			toggleSidebar: () => setSidebar(!sidebar),
																		}}
																	>
																		{InboxManagerItem}
																	</VariableSizeList>
																)}
															</AutoSizer>
														</TableContents>
													</Table>
												)}
											</Box>
										</Container>
									</Main>
									{sidebar && (
										<Sidebar p={24}>
											<FormikProvider value={formik}>
												<InboxManagerSidebar onExport={() => setExporting(true)} />
											</FormikProvider>
										</Sidebar>
									)}
								</Split>
							</IntercomTarget>
						)}
					</Box>
				)}
			</AutoSizer>
			{exporting && (
				<InboxManagerCsvExport
					ids={filtered.map(({ id }) => id)}
					dateRange={dateRange}
					onFinish={() => setExporting(false)}
				/>
			)}
		</>
	);
}
